/* eslint-disable @typescript-eslint/no-explicit-any */
import { isApiError, type Client } from '@botpress/client'
import type { QuotaType } from '@bpinternal/const'
import {
  infiniteQueryOptions,
  queryOptions,
  useQuery as useTanstackQuery,
  useSuspenseInfiniteQuery as useTanstackSuspenseInfiniteQuery,
  useSuspenseQuery as useTanstackSuspenseQuery,
} from '@tanstack/react-query'
import axios from 'axios'
import semver from 'semver'
import { BillingApiClient } from '~/features/billing/services'
import { listBots } from '~/features/bots/services'
import {
  getSubscription,
  getSubscriptions,
  getSubscriptionSchedule,
  listWorkspaceMembers,
  listWorkspaces,
  getAutoRechargeSettings,
} from '~/features/workspaces/services'
import type { Flatten, PathParams } from '~/services/types'
import { cacheDuration } from '~/shared'
import { listAll } from '~/utils'
import { getApiClient, getHitlUserKey } from '../stores'
import { AccountPreferences, getAccountPreference, GetAccountPreferenceProps } from './preferences/userPreferences'
import {
  getWorkspacePreference,
  GetWorkspacePreferenceProps,
  WorkspacePreferences,
} from './preferences/workspacePreferences'
import { BillingInfo, ProductWithPrices, PromoCode } from '../features/billing/types'
type FlattenedKeys = Flatten<typeof queryKeys>
/**
 * This is where we define query keys, keys are created by using the path of leaf nodes. To include a parameter in the key, use $ before the parameter name.
 * If you want to include a parent node in the key, use _ after the parent node name.
 * When a key is added, you will get a type error if you don't implement the corresponding query function in the queryFunctions object.
 */
const queryKeys = {
  preferences: {
    $path: null,
  },
  workspaces_: {
    $workspaceId_: {
      preferences: {
        $path: null,
      },
      bots_: {
        $botId_: {
          integrations: {
            $integrationName: {
              $version: null,
            },
          },
          webchat: null,
          states: {
            $name: null,
          },
          conversations: {
            $integrationName: { $tags: { $participantIds: { all: null } } },
          },
          hitl: {
            $webhookId: {
              userKey: null,
            },
          },
          $conversationId_: { users: null },
          files: { $ids: { $tags: { all: null } }, $id: null },
          knowledgeBases_: {
            $kbId_: {
              sources: { $tags: { all: null } },
            },
          },
          tables: {
            $table_: {
              rows: {
                $filter: { $limit: { $group: { $offset: { $orderBy: { $orderDirection: { $search: null } } } } } },
              },
            },
            $tags: { all: null },
          },
          allowlist: null,
          $userId_: {},
        },
      },
      members: null,
      billing: {
        autoRecharge: null,
        info: null,
        products: null,
        promoCode: null,
        subscription: null,
        subscriptions: null,
        subscriptionSchedule: null,
        promoCodeSearch: {
          $query: null,
        },
      },
      usage: {
        activities: {
          $type: {
            $startDate: { $endDate: null },
            daily: {
              $startDate: { $endDate: null },
            },
          },
        },
      },
    },
  },
  integrations_: { $integrationName: { versions: null } },
  plugins_: { $pluginName_: { versions: null } },
  fetch: { $url: null },
} as const
export const queryFunctions = {
  'workspaces_/$workspaceId_/bots_/$botId_/conversations/$integrationName/$tags/$participantIds/all': async ({
    botId,
    workspaceId,
    integrationName,
    participantIds,
    tags,
  }: {
    botId: string
    workspaceId: string
    participantIds?: string[]
    tags?: Record<string, string>
    integrationName?: string
  }) => getApiClient({ workspaceId, botId }).list.conversations({ integrationName, participantIds, tags }).collect(),
  'preferences/$path': <U extends keyof AccountPreferences>(props: GetAccountPreferenceProps<U>) =>
    getAccountPreference(props),
  workspaces_: listWorkspaces,
  'workspaces_/$workspaceId_': ({ workspaceId }: { workspaceId: string }) =>
    getApiClient({ workspaceId }).getWorkspace({ id: workspaceId }),
  'workspaces_/$workspaceId_/preferences/$path': <U extends keyof WorkspacePreferences>(
    props: { workspaceId: string } & GetWorkspacePreferenceProps<U>
  ) => getWorkspacePreference(props, props.workspaceId),
  'workspaces_/$workspaceId_/members': ({ workspaceId }: { workspaceId: string }) => listWorkspaceMembers(workspaceId),
  'workspaces_/$workspaceId_/billing/subscription': ({ workspaceId }: { workspaceId: string }) =>
    getSubscription(workspaceId),
  'workspaces_/$workspaceId_/billing/autoRecharge': ({ workspaceId }: { workspaceId: string }) =>
    getAutoRechargeSettings(workspaceId),
  'workspaces_/$workspaceId_/billing/subscriptions': ({ workspaceId }: { workspaceId: string }) =>
    getSubscriptions(workspaceId),
  'workspaces_/$workspaceId_/billing/subscriptionSchedule': ({ workspaceId }: { workspaceId: string }) =>
    getSubscriptionSchedule(workspaceId),
  'workspaces_/$workspaceId_/billing/info': async ({ workspaceId }: { workspaceId: string }): Promise<BillingInfo> => {
    return await BillingApiClient.getBillingInfo(workspaceId)
  },
  'workspaces_/$workspaceId_/billing/products': async ({
    workspaceId,
  }: {
    workspaceId: string
  }): Promise<ProductWithPrices[]> => {
    return await BillingApiClient.getBillingProducts(workspaceId)
  },
  'workspaces_/$workspaceId_/billing/promoCode': async ({
    workspaceId,
  }: {
    workspaceId: string
  }): Promise<PromoCode | null> => {
    return await BillingApiClient.getPromoCode(workspaceId)
  },
  'workspaces_/$workspaceId_/billing/promoCodeSearch/$query': async ({
    workspaceId,
    query,
  }: {
    workspaceId: string
    query: string
  }): Promise<PromoCode> => {
    return await BillingApiClient.searchPromoCode({ workspaceId, query })
  },
  'workspaces_/$workspaceId_/bots_': ({ workspaceId }: { workspaceId: string }) => listBots(workspaceId),
  'workspaces_/$workspaceId_/bots_/$botId_': async ({ botId, workspaceId }: { botId: string; workspaceId: string }) =>
    (await getApiClient({ workspaceId }).getBot({ id: botId })).bot,
  'workspaces_/$workspaceId_/bots_/$botId_/states/$name': async ({
    botId,
    workspaceId,
    name,
    type,
  }: {
    botId: string
    workspaceId: string
    name: string
    type: 'conversation' | 'user' | 'bot' | 'integration' | 'task' | 'workflow'
  }) => {
    const client = getApiClient({ workspaceId, botId })
    try {
      return await client.getOrSetState({
        id: botId,
        type,
        name,
        payload: {},
      })
    } catch (error) {
      if (type === 'bot' && isApiError(error) && error.code === 400 && error.type === 'ReferenceNotFound') {
        await client.updateBot({
          id: botId,
          states: {
            [name]: {
              type: 'bot',
              schema: {},
            },
          },
        })
        return await client.setState({
          id: botId,
          type,
          name,
          payload: {},
        })
      } else {
        throw error
      }
    }
  },
  'workspaces_/$workspaceId_/bots_/$botId_/files/$id': async ({
    workspaceId,
    botId,
    id,
  }: {
    workspaceId: string
    botId: string
    id: string
  }) => getApiClient({ workspaceId, botId }).getFile({ id }),
  'workspaces_/$workspaceId_/bots_/$botId_/files/$ids/$tags/all': async ({
    workspaceId,
    botId,
    tags,
    ids,
  }: {
    ids?: string[]
    botId: string
    workspaceId: string
    tags?: Record<string, string>
  }) => listAll(getApiClient({ workspaceId, botId }).listFiles, { tags, ids }, ({ files }) => files),
  'workspaces_/$workspaceId_/bots_/$botId_/knowledgeBases_': async ({
    workspaceId,
    botId,
    nextToken,
  }: {
    workspaceId: string
    botId: string
    nextToken?: string
  }) => getApiClient({ workspaceId, botId }).listKnowledgeBases({ nextToken }),
  'workspaces_/$workspaceId_/bots_/$botId_/knowledgeBases_/$kbId_': async ({
    workspaceId,
    botId,
    kbId,
  }: {
    workspaceId: string
    botId: string
    kbId: string
  }) => {
    const kbs = await listAll(
      getApiClient({ workspaceId, botId }).listKnowledgeBases,
      {},
      ({ knowledgeBases }) => knowledgeBases
    )

    const kb = kbs.find((kb) => kb.id === kbId)
    if (!kb) {
      throw new Error(`Knowledge base with id ${kbId} not found`)
    }
    return kb
  },
  'workspaces_/$workspaceId_/bots_/$botId_/knowledgeBases_/$kbId_/sources/$tags/all': async ({
    workspaceId,
    botId,
    kbId,
    tags,
    nextToken,
  }: {
    workspaceId: string
    botId: string
    kbId: string
    tags?: Record<string, string>
    nextToken?: string
  }) => {
    return getApiClient({ workspaceId, botId }).listFiles({ tags: { kbId: kbId, ...tags }, nextToken })
  },
  'workspaces_/$workspaceId_/bots_/$botId_/$conversationId_': async ({
    workspaceId,
    botId,
    conversationId,
  }: {
    botId: string
    conversationId: string
    workspaceId: string
  }) => getApiClient({ workspaceId, botId }).getConversation({ id: conversationId }),
  'workspaces_/$workspaceId_/bots_/$botId_/$conversationId_/users': async ({
    workspaceId,
    botId,
    conversationId,
  }: {
    botId: string
    conversationId: string
    workspaceId: string
  }) => getApiClient({ workspaceId, botId }).listUsers({ conversationId }),
  'workspaces_/$workspaceId_/bots_/$botId_/$userId_': async ({
    workspaceId,
    botId,
    userId,
  }: {
    botId: string
    userId: string
    workspaceId: string
  }) => getApiClient({ workspaceId, botId }).getUser({ id: userId }),
  'workspaces_/$workspaceId_/bots_/$botId_/webchat': async ({
    workspaceId,
    botId,
    type,
  }: {
    workspaceId: string
    botId: string
    type: Parameters<Client['getBotWebchat']>[0]['type']
  }) => {
    const script = (await getApiClient({ workspaceId }).getBotWebchat({ id: botId, type })).code
    return script
  },
  'workspaces_/$workspaceId_/bots_/$botId_/integrations/$integrationName/$version': async ({
    workspaceId,
    botId,
    integrationName,
    version,
  }: {
    workspaceId: string
    botId: string
    integrationName: string
    version: string
  }) => getApiClient({ workspaceId, botId }).getPublicIntegration({ name: integrationName, version }),
  integrations_: () => listAll(getApiClient().listPublicIntegrations, { version: 'latest' }, (res) => res.integrations),
  'integrations_/$integrationName/versions': ({ integrationName }: { integrationName: string }) =>
    getApiClient()
      .listPublicIntegrations({ name: integrationName })
      .then((res) => [...res.integrations].sort((a, b) => (semver.gt(a.version, b.version) ? -1 : 1))),
  'fetch/$url': async ({ url }: { url: string }) => {
    const file = await axios.get(url, {
      withCredentials: false,
    })

    return file?.data ?? ''
  },
  'workspaces_/$workspaceId_/bots_/$botId_/tables/$table_': async ({
    workspaceId,
    botId,
    table,
  }: {
    workspaceId: string
    botId: string
    table: string
  }) => getApiClient({ workspaceId, botId }).getTable({ table: table }),
  'workspaces_/$workspaceId_/bots_/$botId_/tables/$tags/all': async ({
    workspaceId,
    botId,
    tags,
  }: {
    workspaceId: string
    botId: string
    tags: Record<string, string>
  }) => getApiClient({ workspaceId, botId }).listTables({ tags }),
  'workspaces_/$workspaceId_/bots_/$botId_/tables/$table_/rows/$filter/$limit/$group/$offset/$orderBy/$orderDirection/$search':
    async ({
      workspaceId,
      botId,
      ...rest
    }: {
      workspaceId: string
      botId: string
      table: string
      filter?: Record<string, any>
      limit?: number
      group?: Record<string, any>
      offset?: number
      orderBy?: string
      orderDirection?: 'asc' | 'desc'
      search?: string
    }) => {
      const client = getApiClient({ workspaceId, botId })
      return client.findTableRows(rest)
    },
  'workspaces_/$workspaceId_/usage/activities/$type/daily/$startDate/$endDate': async ({
    workspaceId,
    type,
    startDate,
    endDate,
  }: {
    workspaceId: string
    type: QuotaType
    startDate?: string
    endDate?: string
  }) => {
    return getApiClient({ workspaceId })
      .list.usageActivityDaily({
        id: workspaceId,
        type,
        dateFrom: startDate,
        dateUntil: endDate,
      })
      .collect()
  },
  'workspaces_/$workspaceId_/usage/activities/$type/$startDate/$endDate': async ({
    workspaceId,
    type,
    startDate,
    endDate,
  }: {
    workspaceId: string
    type: QuotaType
    startDate?: string
    endDate?: string
  }) => {
    return getApiClient({ workspaceId })
      .list.usageActivity({
        id: workspaceId,
        type,
        timestampFrom: startDate,
        timestampUntil: endDate,
      })
      .collect()
  },
  'workspaces_/$workspaceId_/bots_/$botId_/hitl/$webhookId/userKey': async ({
    webhookId,
  }: {
    workspaceId: string
    botId: string
    webhookId: string
  }) => {
    const userKey = getHitlUserKey(webhookId)
    return userKey
  },
  'workspaces_/$workspaceId_/bots_/$botId_/allowlist': async ({
    botId,
    workspaceId,
  }: {
    botId: string
    workspaceId: string
  }) => {
    return getApiClient({ workspaceId, botId }).getBotAllowlist({
      botId,
    })
  },
  plugins_: () => listAll(getApiClient().listPublicPlugins, { version: 'latest' }, (res) => res.plugins),
  'plugins_/$pluginName_': ({ pluginName }: { pluginName: string }) =>
    getApiClient()
      .getPublicPlugin({ name: pluginName, version: 'latest' })
      .then((res) => res.plugin),
  'plugins_/$pluginName_/versions': ({ pluginName }: { pluginName: string }) =>
    getApiClient()
      .listPublicPlugins({ name: pluginName })
      .then((res) => [...res.plugins].sort((a, b) => (semver.gt(a.version, b.version) ? -1 : 1))),
} as const satisfies Record<FlattenedKeys, (props: any) => Promise<any> | any>

export const getQueryKey = <T extends FlattenedKeys>(
  path: T,
  ...param: [PathParams<T>] extends [never] ? [] : [PathParams<T>]
) => {
  const [paramsOjb] = param

  return path.split('/').map((key) => {
    return key.startsWith('$') ? (paramsOjb as Record<string, any>)[key.replace(/^\$|_$/g, '')] : key
  })
}

type QueryFunctionParams<T extends FlattenedKeys> = Parameters<(typeof queryFunctions)[T]>[0]

type ParamType<T extends FlattenedKeys> = [PathParams<T>] extends [never]
  ? QueryFunctionParams<T> extends undefined
    ? []
    : [QueryFunctionParams<T>]
  : QueryFunctionParams<T> extends undefined
    ? [PathParams<T>]
    : [QueryFunctionParams<T>]

export const getQueryOptions = <T extends FlattenedKeys>(path: T, ...param: ParamType<T>) => {
  const [paramsOjb] = param

  const queryKey = path.split('/').map((key) => {
    return key.startsWith('$') ? (paramsOjb as Record<string, any>)[key.replace(/^\$|_$/g, '')] : key
  })

  const queryFunction = queryFunctions[path]

  return queryOptions({
    queryKey,
    enabled:
      paramsOjb &&
      Object.values(paramsOjb as Record<string, unknown>).every((value) => value !== undefined && value !== ''),
    queryFn: (): Awaited<ReturnType<typeof queryFunction>> => {
      const fn = queryFunction as (...args: any[]) => any
      return fn(paramsOjb)
    },
    staleTime: cacheDuration.short,
    meta: {
      suppressError: true,
    },
  })
}

export const getInfiniteQueryOptions = <T extends FlattenedKeys>(path: T, ...param: ParamType<T>) => {
  const [paramsOjb] = param

  const queryKey = path.split('/').map((key) => {
    return key.startsWith('$') ? (paramsOjb as Record<string, any>)[key.replace(/^\$|_$/g, '')] : key
  })

  const queryFunction = queryFunctions[path]

  return infiniteQueryOptions({
    queryKey,
    enabled:
      paramsOjb &&
      Object.values(paramsOjb as Record<string, unknown>).every((value) => value !== undefined && value !== ''),
    queryFn: ({ pageParam }): Awaited<ReturnType<typeof queryFunction>> => {
      const fn = queryFunction as (...args: any[]) => any
      return fn({ ...paramsOjb, nextToken: pageParam })
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.meta.nextToken,
    staleTime: cacheDuration.short,
    meta: {
      suppressError: true,
    },
  })
}
/**
 * This is a custom hook that is used to fetch data from the server using the functions defined in the queryFunctions object.
 *  It is a wrapper around the useQuery hook from react-query.
 */
export const useQuery = <T extends FlattenedKeys>(...props: Parameters<typeof getQueryOptions<T>>) =>
  useTanstackQuery(getQueryOptions(...props))
/**
 * This is a custom hook that is used to fetch data from the server using the functions defined in the queryFunctions object.
 *  It is a wrapper around the useQuery hook from react-query.
 */
export const useSuspenseQuery = <T extends FlattenedKeys>(...props: Parameters<typeof getQueryOptions<T>>) =>
  useTanstackSuspenseQuery(getQueryOptions(...props))

export const useSuspenseInfiniteQuery = <T extends FlattenedKeys>(...props: Parameters<typeof getQueryOptions<T>>) =>
  useTanstackSuspenseInfiniteQuery(getInfiniteQueryOptions(...props))
